import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { autoUpdate, flip, FloatingPortal, offset, shift, useClick, useDismiss, useFloating, useInteractions } from '@floating-ui/react';
import { Breakpoint, useBreakpointUp } from '@travel-frontend/core';
import { useState } from 'react';
import { useHotlineInformation } from '../../components/hooks/useHotlineInformation.js';
import { usePhoneNumberNew } from '../../components/hooks/usePhoneNumberNext.js';
import { Content } from '../../components/HotlineBarNew/components/Content.js';
import { Hotline } from '../../components/HotlineBarNew/components/Hotline.js';
import { Information } from '../../components/HotlineBarNew/components/Information/index.js';
import { Dropdown } from '../../components/internal/Dropdown/index.js';
import { Dialog, DialogBody, DialogHead, Fade } from '../../primitives/index.js';
import { HEADER_QA_ID, qaId } from '../../utils/qaId.js';
export const HotlineBarContent = ({
  config,
  dropdownPlacement = 'bottom-end'
}) => {
  const lgUp = useBreakpointUp('lg');
  const [infoVisible, setInfoVisible] = useState(false);
  const {
    refs,
    context,
    floatingStyles
  } = useFloating({
    open: infoVisible,
    onOpenChange: setInfoVisible,
    middleware: [offset(5), flip(), shift()],
    placement: dropdownPlacement,
    whileElementsMounted: autoUpdate
  });
  const {
    getReferenceProps,
    getFloatingProps
  } = useInteractions([useClick(context), useDismiss(context, {
    // event handlers would still fire despite not even being applied to the reference below `lgUp` 🤔
    enabled: lgUp
  })]);
  const {
    numbers,
    image,
    headline,
    subHeadline,
    teaser
  } = config || {};
  const phone = usePhoneNumberNew(numbers);
  const showInfo = () => setInfoVisible(true);
  const hideInfo = () => setInfoVisible(false);
  const {
    short,
    long,
    teaserSpecialImage,
    specialHeadline
  } = useHotlineInformation(config);
  const openingHoursShort = `${short.from}-${short.to}`;
  const openingHoursLong = `${long.from} – ${long.to}`;
  return _jsxs(Content, {
    children: [_jsxs(Breakpoint, {
      matches: ['lg', 'xl'],
      children: [_jsx(Hotline, {
        ref: refs.setReference,
        ...getReferenceProps(),
        title: teaser,
        openingHours: openingHoursShort,
        children: phone
      }), _jsx(FloatingPortal, {
        children: _jsx(Fade, {
          in: infoVisible,
          children: _jsx(Dropdown, {
            "data-cy": qaId(HEADER_QA_ID, 'hotline', 'floatingportal'),
            "data-testid": qaId(HEADER_QA_ID, 'hotline', 'floatingportal'),
            ref: refs.setFloating,
            style: floatingStyles,
            ...getFloatingProps(),
            children: _jsx(Information, {
              specials: config?.specials,
              teaserImage: teaserSpecialImage || image,
              headline: specialHeadline || headline,
              openingHours: openingHoursLong,
              phone: phone,
              subHeadline: subHeadline,
              onClose: hideInfo
            })
          })
        })
      })]
    }), _jsxs(Breakpoint, {
      matches: ['xs', 'sm', 'md'],
      children: [_jsx(Hotline, {
        onClick: showInfo,
        title: teaser,
        openingHours: openingHoursShort,
        children: phone
      }), _jsxs(Dialog, {
        open: infoVisible,
        onClose: hideInfo,
        children: [_jsx(DialogHead, {
          onClose: hideInfo
        }), _jsx(DialogBody, {
          children: _jsx(Information, {
            specials: config?.specials,
            teaserImage: teaserSpecialImage || image,
            headline: specialHeadline || headline,
            openingHours: openingHoursLong,
            phone: phone,
            subHeadline: subHeadline,
            onClose: hideInfo
          })
        })]
      })]
    })]
  });
};